import { AsyncQueue } from "../async";
import { concat, fromBase64, fromUtf8, toBase64, utf8 } from "../bytes";
import { HermesError } from "../errors";
import type { GatewaySocket, HermesTransport, HttpRequest, HttpResponse, SocketFrame } from "../transport";
import { RelayCipher, RelayFrame, RelayHandshake } from "./crypto";
import type { RelayKeyStore } from "./keystore";
import { clientURL, hostKeyBytes, keyAccount, parseWelcome, type RelayRoute, type RelayWelcome } from "./route";

/** 浏览器自带的 WebSocket；测试时可以换成别的实现。 */
export type WebSocketFactory = (url: string) => WebSocket;

interface Inbound {
  t: string;
  id?: number;
  s?: number;
  h?: string[][];
  b?: string;
  end?: boolean;
  e?: string;
  d?: string;
  code?: number;
  r?: string;
}

interface Outbound {
  t: string;
  id?: number;
  m?: string;
  p?: string;
  h?: string[][];
  b?: string;
  d?: string;
}

interface PendingHTTP {
  status: number;
  headers: Record<string, string>;
  chunks: Uint8Array[];
  resolve: (r: HttpResponse) => void;
  reject: (e: unknown) => void;
  timer?: ReturnType<typeof setTimeout>;
}

/**
 * 经 TimiBot 中转到一台主机（connector）的端到端加密隧道（对应 HermesKit 的 RelayTunnel）。
 *
 * 一条到中转的 WebSocket 上多路复用：
 * - HTTP 请求（Hermes 的 REST、登录、文件下载）→ connector 转给本机 hermes serve
 * - 对话 WebSocket（/api/ws）→ connector 在本机开一条 WS，逐帧转发
 *
 * 隧道断开时，进行中的请求报错、对话 WebSocket 关闭——上层 HermesClient 的重连逻辑会重新走一遍。
 */
export class RelayTunnel implements HermesTransport {
  private connecting?: Promise<RelayWelcome>;
  private ready?: RelayWelcome;
  private wire?: WebSocket;
  private cipher?: RelayCipher;
  private inbox: Uint8Array[] = [];
  private keepaliveTimer?: ReturnType<typeof setInterval>;
  private lastInbound = Date.now();
  private lastPingAt = 0;
  private nextID = 0;
  /** 每次 teardown 加一：握手途中被取消时，旧的握手结果不能再装上 */
  private generation = 0;

  private requests = new Map<number, PendingHTTP>();
  private opening = new Map<number, { resolve: () => void; reject: (e: unknown) => void }>();
  private sockets = new Map<number, RelaySocket>();

  welcome?: RelayWelcome;

  constructor(
    readonly route: RelayRoute,
    private readonly keyStore: RelayKeyStore,
    private readonly deviceName: string,
    private readonly makeSocket: WebSocketFactory = (url) => new WebSocket(url),
  ) {}

  get isReady(): boolean { return this.ready !== undefined; }

  // MARK: 建立隧道

  /** 确保隧道可用；需要时连接中转并完成握手。`pairCode` 只在首次配对时需要。 */
  connect(pairCode?: string): Promise<RelayWelcome> {
    if (this.ready) return Promise.resolve(this.ready);
    if (this.connecting) return this.connecting;
    const gen = this.generation;
    const task = this.establish(pairCode, gen).finally(() => {
      if (this.connecting === task) this.connecting = undefined;
    });
    this.connecting = task;
    return task;
  }

  /** 用配对码重新握手（输入配对码配对时调用）。 */
  pair(code?: string): Promise<RelayWelcome> {
    this.teardown(HermesError.cancelled());
    return this.connect(code);
  }

  /** 主动断开（页面进后台很久、切换连接）。 */
  suspend(): void {
    this.teardown(HermesError.transport("连接已暂停"));
  }

  /** 回到前台：如果隧道其实已经死了（系统挂起过），尽快发现并断开，让上层重连。 */
  checkAlive(): void {
    if (!this.ready || !this.wire) return;
    const sentAt = Date.now();
    this.sendText("ping");
    this.lastPingAt = sentAt;
    setTimeout(() => {
      if (this.ready && this.lastInbound < sentAt) this.teardown(HermesError.transport("中转连接没有响应"));
    }, 5000);
  }

  private async establish(pairCode: string | undefined, gen: number): Promise<RelayWelcome> {
    const key = this.keyStore.clientKey(keyAccount(this.route));
    const handshake = new RelayHandshake(key, hostKeyBytes(this.route));
    const wire = this.makeSocket(clientURL(this.route));
    wire.binaryType = "arraybuffer";
    const name = this.route.name;
    let cleanup = () => {};
    try {
      const welcome = await new Promise<{ cipher: RelayCipher; info: Uint8Array }>((resolve, reject) => {
        let opened = false;
        const openTimer = setTimeout(() => reject(HermesError.transport("连不上 TimiBot 中转服务：连接超时")), 15000);
        let replyTimer: ReturnType<typeof setTimeout> | undefined;
        const onOpen = () => {
          opened = true;
          clearTimeout(openTimer);
          const hello = utf8(JSON.stringify({ v: 1, ps: toBase64URLString(handshake.clientPublicKey), pair: pairCode ?? null, device: this.deviceName }));
          try { wire.send(handshake.hello(hello) as Uint8Array<ArrayBuffer>); } catch (e) { reject(e); return; }
          replyTimer = setTimeout(() => reject(HermesError.transport(`主机「${name}」没有响应，请确认 connector 在运行`)), 15000);
        };
        const onMessage = (ev: MessageEvent) => {
          if (typeof ev.data === "string") {
            if (ev.data.includes('"online":false')) {
              reject(HermesError.transport(`「${name}」离线：主机上的 connector 没有运行，或者电脑在睡眠`));
            }
            return;
          }
          const data = new Uint8Array(ev.data as ArrayBuffer);
          if (data.length === 0) return;
          if (data[0] === RelayFrame.reject) {
            let reason = "";
            try { reason = JSON.parse(fromUtf8(data.subarray(1)))?.error ?? ""; } catch { /* 忽略 */ }
            if (reason === "pair_expired") {
              reject(HermesError.authenticationRequired("配对码已过期或已被使用，请在主机上运行 pair 重新生成配对码"));
            } else {
              reject(HermesError.authenticationRequired(`这台设备还没有和「${name}」配对，或已被取消授权。请在主机上运行 pair 后重新输入配对码；如果是朋友分享的 Bot，请对方重新邀请`));
            }
            return;
          }
          try {
            const { cipher, welcome: info } = handshake.finish(data);
            resolve({ cipher, info });
          } catch (e) {
            reject(e);
          }
        };
        const onClose = (ev: CloseEvent) => {
          reject(closeReason(ev.code, name) ?? HermesError.transport(opened ? "中转连接已断开" : "连不上 TimiBot 中转服务"));
        };
        const onError = () => { if (!opened) reject(HermesError.transport("连不上 TimiBot 中转服务")); };
        wire.addEventListener("open", onOpen);
        wire.addEventListener("message", onMessage);
        wire.addEventListener("close", onClose);
        wire.addEventListener("error", onError);
        cleanup = () => {
          clearTimeout(openTimer);
          if (replyTimer) clearTimeout(replyTimer);
          wire.removeEventListener("open", onOpen);
          wire.removeEventListener("message", onMessage);
          wire.removeEventListener("close", onClose);
          wire.removeEventListener("error", onError);
        };
      });
      cleanup();
      let info: unknown = {};
      try { info = JSON.parse(fromUtf8(welcome.info)); } catch { /* 老版本：空 */ }
      // 握手期间可能已被 suspend()/pair() 取消，这时不能再把旧连接装上
      if (gen !== this.generation) throw HermesError.cancelled();
      const w = parseWelcome(info);
      this.install(wire, welcome.cipher, w);
      return w;
    } catch (e) {
      cleanup();
      try { wire.close(); } catch { /* 已关闭 */ }
      throw e;
    }
  }

  private install(wire: WebSocket, cipher: RelayCipher, welcome: RelayWelcome): void {
    this.wire = wire;
    this.cipher = cipher;
    this.welcome = welcome;
    this.ready = welcome;
    this.inbox = [];
    this.lastInbound = Date.now();
    this.lastPingAt = 0;
    wire.addEventListener("message", (ev) => this.received(ev, wire));
    wire.addEventListener("close", (ev) => {
      if (wire !== this.wire) return;
      this.teardown(closeReason(ev.code, this.route.name) ?? HermesError.transport("中转连接已断开"));
    });
    // 保活：每 20 秒一个文本 ping（中转直接回 pong）。判断“没响应”只看上一次 ping 之后有没有收到任何东西——
    // 浏览器把后台标签页的定时器降到一分钟一次时，不能因为两次 ping 间隔变长就误判断线。
    this.keepaliveTimer = setInterval(() => {
      if (wire !== this.wire) return;
      const now = Date.now();
      if (this.lastPingAt > 0 && this.lastInbound < this.lastPingAt && now - this.lastPingAt > 30000) {
        this.teardown(HermesError.transport("中转连接没有响应"));
        return;
      }
      this.sendText("ping");
      this.lastPingAt = now;
    }, 20000);
  }

  /** 隧道失效：所有进行中的请求报错，对话 WebSocket 关闭。 */
  private teardown(error: unknown): void {
    this.generation += 1;
    this.connecting = undefined;
    this.ready = undefined;
    if (this.keepaliveTimer) clearInterval(this.keepaliveTimer);
    this.keepaliveTimer = undefined;
    const wire = this.wire;
    this.wire = undefined;
    this.cipher = undefined;
    this.inbox = [];
    try { wire?.close(); } catch { /* 已关闭 */ }
    const requests = [...this.requests.values()];
    this.requests.clear();
    for (const r of requests) { if (r.timer) clearTimeout(r.timer); r.reject(error); }
    const opening = [...this.opening.values()];
    this.opening.clear();
    for (const o of opening) o.reject(error);
    const sockets = [...this.sockets.values()];
    this.sockets.clear();
    for (const s of sockets) s.finish(error);
  }

  // MARK: 收发

  private sendText(text: string): void {
    try { this.wire?.send(text); } catch { /* 关闭中 */ }
  }

  private post(message: Outbound): void {
    const cipher = this.cipher;
    const wire = this.wire;
    if (!cipher || !wire) throw HermesError.notConnected();
    for (const frame of cipher.seal(utf8(JSON.stringify(message)))) wire.send(frame as Uint8Array<ArrayBuffer>);
  }

  private received(ev: MessageEvent, from: WebSocket): void {
    if (from !== this.wire) return;
    this.lastInbound = Date.now();
    if (typeof ev.data === "string" || !this.cipher) return;
    let opened: { kind: number; plain: Uint8Array };
    try {
      opened = this.cipher.open(new Uint8Array(ev.data as ArrayBuffer));
    } catch {
      this.teardown(HermesError.transport("隧道数据校验失败，已断开"));
      return;
    }
    this.inbox.push(opened.plain);
    if (opened.kind !== RelayFrame.final) return;
    const data = this.inbox.length === 1 ? this.inbox[0] : concat(...this.inbox);
    this.inbox = [];
    let msg: Inbound;
    try { msg = JSON.parse(fromUtf8(data)); } catch { return; }
    this.dispatch(msg);
  }

  private dispatch(msg: Inbound): void {
    const id = msg.id;
    if (typeof id !== "number") return;
    switch (msg.t) {
      case "res": {
        const r = this.requests.get(id);
        if (!r) return;
        r.status = msg.s ?? 0;
        for (const pair of msg.h ?? []) {
          if (pair.length !== 2) continue;
          const k = pair[0].toLowerCase();
          r.headers[k] = r.headers[k] !== undefined ? `${r.headers[k]}, ${pair[1]}` : pair[1];
        }
        if (msg.b) { const b = fromBase64(msg.b); if (b) r.chunks.push(b); }
        if (msg.end ?? true) this.finishRequest(id);
        return;
      }
      case "chunk": {
        const r = this.requests.get(id);
        if (r && msg.b) { const b = fromBase64(msg.b); if (b) r.chunks.push(b); }
        return;
      }
      case "end":
        this.finishRequest(id);
        return;
      case "err": {
        const r = this.requests.get(id);
        if (!r) return;
        this.requests.delete(id);
        if (r.timer) clearTimeout(r.timer);
        r.reject(HermesError.transport(msg.e ?? "主机上的请求失败"));
        return;
      }
      case "wso": {
        const o = this.opening.get(id);
        this.opening.delete(id);
        o?.resolve();
        return;
      }
      case "wse": {
        this.sockets.delete(id);
        const status = msg.s ?? 0;
        const error = status > 0
          ? HermesError.http(status, "WebSocket 握手被网关拒绝")
          : HermesError.transport(msg.e ?? "主机连不上本机的 Hermes");
        const o = this.opening.get(id);
        this.opening.delete(id);
        o?.reject(error);
        return;
      }
      case "wsm": {
        const s = this.sockets.get(id);
        if (!s) return;
        if (typeof msg.d === "string") s.deliver({ text: msg.d });
        else if (msg.b) { const b = fromBase64(msg.b); if (b) s.deliver({ binary: b }); }
        return;
      }
      case "wsc": {
        const s = this.sockets.get(id);
        this.sockets.delete(id);
        s?.finish(socketCloseError(msg.code ?? 1000, msg.r ?? ""));
        return;
      }
    }
  }

  private finishRequest(id: number): void {
    const r = this.requests.get(id);
    if (!r) return;
    this.requests.delete(id);
    if (r.timer) clearTimeout(r.timer);
    r.resolve({ status: r.status, headers: r.headers, body: r.chunks.length === 1 ? r.chunks[0] : concat(...r.chunks) });
  }

  // MARK: HermesTransport

  async request(req: HttpRequest): Promise<HttpResponse> {
    await this.connect();
    const id = ++this.nextID;
    const message: Outbound = {
      t: "req", id, m: req.method, p: req.path,
      h: Object.entries(req.headers ?? {}).map(([k, v]) => [k, v]),
    };
    if (req.body) message.b = toBase64(req.body);
    const timeout = (req.timeout && req.timeout > 0 ? req.timeout : 60) * 1000;
    return new Promise<HttpResponse>((resolve, reject) => {
      const pending: PendingHTTP = { status: 0, headers: {}, chunks: [], resolve, reject };
      pending.timer = setTimeout(() => {
        if (!this.requests.has(id)) return;
        this.requests.delete(id);
        reject(HermesError.transport("请求超时"));
        try { this.post({ t: "cancel", id }); } catch { /* 隧道已断 */ }
      }, timeout);
      req.signal?.addEventListener("abort", () => {
        if (!this.requests.has(id)) return;
        this.requests.delete(id);
        clearTimeout(pending.timer);
        reject(HermesError.cancelled());
        try { this.post({ t: "cancel", id }); } catch { /* 隧道已断 */ }
      }, { once: true });
      this.requests.set(id, pending);
      try {
        this.post(message);
      } catch (e) {
        this.requests.delete(id);
        clearTimeout(pending.timer);
        reject(e);
      }
    });
  }

  async openSocket(path: string, timeout: number): Promise<GatewaySocket> {
    await this.connect();
    const id = ++this.nextID;
    const socket = new RelaySocket(id, this);
    this.sockets.set(id, socket);
    await new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => {
        if (!this.opening.has(id)) return;
        this.opening.delete(id);
        this.sockets.delete(id);
        reject(HermesError.transport("主机上的 Hermes 没有及时响应"));
        try { this.post({ t: "wsc", id }); } catch { /* 隧道已断 */ }
      }, timeout * 1000);
      this.opening.set(id, {
        resolve: () => { clearTimeout(timer); resolve(); },
        reject: (e) => { clearTimeout(timer); reject(e); },
      });
      try {
        this.post({ t: "ws", id, p: path, h: [] });
      } catch (e) {
        this.sockets.delete(id);
        this.opening.delete(id);
        clearTimeout(timer);
        reject(e);
      }
    });
    return socket;
  }

  /** @internal */
  socketSend(id: number, text: string): void {
    if (!this.sockets.has(id)) throw HermesError.notConnected();
    this.post({ t: "wsm", id, d: text });
  }

  /** @internal 二进制帧（connector 1.8 起）：base64 放在 "b" 里 */
  socketSendBinary(id: number, data: Uint8Array): void {
    if (!this.sockets.has(id)) throw HermesError.notConnected();
    this.post({ t: "wsm", id, b: toBase64(data) });
  }

  /** @internal */
  socketClose(id: number): void {
    if (!this.sockets.delete(id)) return;
    try { this.post({ t: "wsc", id }); } catch { /* 隧道已断 */ }
  }
}

/** 隧道里的一条对话 WebSocket */
class RelaySocket implements GatewaySocket {
  private queue = new AsyncQueue<SocketFrame>();
  private closedError?: unknown;

  constructor(readonly id: number, private readonly tunnel: RelayTunnel) {}

  async send(text: string): Promise<void> {
    if (this.closedError) throw this.closedError;
    this.tunnel.socketSend(this.id, text);
  }

  async sendBinary(data: Uint8Array): Promise<void> {
    if (this.closedError) throw this.closedError;
    this.tunnel.socketSendBinary(this.id, data);
  }

  async receive(): Promise<string> {
    const frame = await this.queue.next();
    return "text" in frame ? frame.text : fromUtf8(frame.binary);
  }

  receiveFrame(): Promise<SocketFrame> {
    return this.queue.next();
  }

  close(): void {
    this.finish(HermesError.cancelled());
    this.tunnel.socketClose(this.id);
  }

  deliver(frame: SocketFrame): void {
    this.queue.push(frame);
  }

  finish(error: unknown): void {
    if (!this.closedError) this.closedError = error;
    this.queue.close(error);
  }
}

function toBase64URLString(bytes: Uint8Array): string {
  return toBase64(bytes).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function closeReason(code: number | undefined, hostName: string): HermesError | undefined {
  switch (code) {
    case 4001: return HermesError.transport(`「${hostName}」刚刚重新上线，正在重连`);
    case 4002: return HermesError.transport(`「${hostName}」离线：主机上的 connector 没有运行，或者电脑在睡眠`);
    case 4003: return HermesError.transport(`「${hostName}」断开了这个连接`);
    default: return undefined;
  }
}

/** 对话 WebSocket 被网关关闭（对应 Swift 的 GatewaySocket.closeError）。 */
export function socketCloseError(code: number, reason: string): HermesError {
  switch (code) {
    case 4401: return HermesError.authenticationRequired(reason || "网关拒绝了凭据 (4401)");
    case 4403: return HermesError.transport(`网关拒绝了该连接的 Host/Origin (4403) ${reason}`);
    case 4404: return HermesError.transport("网关未启用对话通道 (4404)");
    case 4408: return HermesError.transport("网关不接受来自该地址的连接 (4408)，请确认 hermes serve 的绑定地址");
    default: return HermesError.transport(reason ? `连接已关闭 (${code})：${reason}` : `连接已关闭 (${code})`);
  }
}
